import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Eye, CheckCircle2 } from "lucide-react";
import type { BookingResponseUI } from "@/types/activities";

// Utility functions for booking display
const getTypeColor = (type: string) => {
  switch (type) {
    case "marketplace": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "pending": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-400";
    case "negotiating": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:bg-opacity-20 dark:text-yellow-400";
    case "awaiting-approval": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:bg-opacity-20 dark:text-purple-400";
    case "vendor-booked": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:bg-opacity-20 dark:text-orange-400";
    case "confirmed": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    case "cancelled": return "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
    case "rejected": return "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
    case "expired": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:bg-opacity-20 dark:text-gray-400";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

interface BookingsTableProps {
  bookings: BookingResponseUI[];
  selectedBookings: string[];
  onSelectBooking: (id: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onOpenBooking: (booking: BookingResponseUI) => void;
}

export default function BookingsTable({
  bookings,
  selectedBookings,
  onSelectBooking,
  onSelectAll,
  onOpenBooking
}: BookingsTableProps) {
  const allSelected = bookings.length > 0 && selectedBookings.length === bookings.length;
  const someSelected = selectedBookings.length > 0 && selectedBookings.length < bookings.length;

  return (
    <Card className="athlyn-card">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={onSelectAll}
                  aria-label="Select all bookings"
                />
              </TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date Submitted</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Price</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.map((booking) => (
              <TableRow 
                key={booking.id}
                className="cursor-pointer hover:bg-muted hover:bg-opacity-50 transition-colors"
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  const isInteractive = target.closest('button, input, [role="checkbox"]');
                  if (!isInteractive) {
                    onOpenBooking(booking);
                  }
                }}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox 
                    checked={selectedBookings.includes(booking.id)}
                    onCheckedChange={(checked) => onSelectBooking(booking.id, !!checked)}
                    aria-label={`Select ${booking.title}`}
                  />
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{booking.title}</p>
                    <p className="text-sm text-muted-foreground">{booking.description?.substring(0, 50)}{booking.description && booking.description.length > 50 ? '...' : ''}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <img 
                      src={booking.organizationLogo} 
                      alt={booking.organizationName}
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="text-sm">{booking.organizationName}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`${getTypeColor(booking.type)} text-xs`}>
                    {booking.type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {booking.status === 'confirmed' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                    <Badge variant="outline" className={`${getStatusColor(booking.status)} text-xs`}>
                      {booking.status.replace(/-/g, ' ')}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    {new Date(booking.dateSubmitted).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p className="font-medium">{booking.vendorName || 'TBD'}</p>
                    <p className="text-muted-foreground">{booking.vendorPackage || '-'}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-medium">
                    {booking.vendorPrice || 'Pending'}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenBooking(booking);
                    }}
                    className="flex items-center gap-1 ml-auto"
                  >
                    <Eye className="w-4 h-4" />
                    Open
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}